import React from "react";
import { Box, Button, Typography } from "@mui/material";
import {
  LockOutlined as LockIcon,
  Home as HomeIcon,
  ArrowForward as BackIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { RTL } from "../../cache/CacheRtl";
import { useAuth } from "../../contexts/AuthContext";
import { colors, borders } from "../ui/colors";

/**
 * Shown when the server answers a by-id page with 403 — the row exists, the caller just isn't
 * scoped to it. Deliberately NOT the 404 screen: "does not exist" is a lie that sends the user
 * hunting for a bug, and the actionable fact is *why* they're blocked.
 *
 * The dominant case is a company-less SYSTEM_ADMIN opening a bookmarked client URL without an
 * active impersonation: /admin/** excludes SYSTEM_ADMIN by design, so every request 403s. For
 * them the way out is to impersonate, not to go home — so the copy says so.
 *
 * Callers must render this INSTEAD of the page body (early return), not alongside it. Leaving the
 * shell mounted lets child sections fire their own doomed requests, which is what produced the
 * half-rendered page with a spinner that never resolves.
 */
export interface AccessDeniedScreenProps {
  /** Overrides the auto-selected explanation. */
  message?: string;
  /** Where "back to safety" goes. Defaults to the clients list. */
  backTo?: string;
  /** Label for the back button. */
  backLabel?: string;
}

const AccessDeniedScreen = ({
  message,
  backTo = "/app/clients",
  backLabel = "חזרה ללקוחות",
}: AccessDeniedScreenProps) => {
  const navigate = useNavigate();
  const { isSuperAdmin, isImpersonating } = useAuth();

  // A super-admin who hasn't impersonated is blocked by design, not by a mistake — tell them the
  // fix. Everyone else (a trainer opening another trainer's trainee) gets the plain refusal.
  const needsImpersonation = isSuperAdmin() && !isImpersonating();

  const explanation =
    message ??
    (needsImpersonation
      ? "לקוח זה שייך לחברה אחרת. כדי לצפות בפרטיו יש להתחזות למשתמש בחברה שלו."
      : "אין לך הרשאה לצפות בעמוד זה. אם לדעתך מדובר בטעות, פנה למנהל המערכת.");

  return (
    <RTL>
      <Box
        dir="rtl"
        data-testid="access-denied-screen"
        sx={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          padding: 3,
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: colors.warningBg,
          }}
        >
          <LockIcon sx={{ fontSize: 34, color: colors.warning }} />
        </Box>

        <Typography sx={{ fontSize: "22px", fontWeight: 700, color: colors.textPrimary }}>
          אין לך הרשאה לבצע פעולה זו
        </Typography>
        <Typography sx={{ fontSize: "14px", color: colors.textSecondary, maxWidth: 420 }}>
          {explanation}
        </Typography>

        <Box sx={{ display: "flex", gap: 1.5, mt: 1, flexWrap: "wrap", justifyContent: "center" }}>
          <Button
            variant="contained"
            startIcon={<BackIcon />}
            onClick={() => navigate(backTo)}
            sx={{
              backgroundColor: colors.primary,
              textTransform: "none",
              borderRadius: borders.radius.md,
              padding: "8px 22px",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": { backgroundColor: colors.primaryDark, boxShadow: "none" },
            }}
          >
            {backLabel}
          </Button>
          <Button
            variant="outlined"
            startIcon={<HomeIcon />}
            onClick={() => navigate("/app/dashboard")}
            sx={{
              borderColor: colors.border,
              color: colors.textSecondary,
              textTransform: "none",
              borderRadius: borders.radius.md,
              padding: "8px 22px",
              fontWeight: 600,
              "&:hover": { borderColor: colors.borderHover, backgroundColor: colors.bgHover },
            }}
          >
            חזרה לדשבורד
          </Button>
        </Box>
      </Box>
    </RTL>
  );
};

export default AccessDeniedScreen;
